import React, { useState, useCallback } from "react";
import {
  ModalOverlay,
  ModalContainer,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ProgressBar,
  ProgressTrack,
  ProgressFill,
  ProgressLabel,
  Section,
  SectionHeader,
  SectionNumber,
  SectionTitle,
  Chevron,
  SectionBody,
  SectionContent,
  NoteBox,
  AcceptBox,
  CustomCheck,
  AcceptLabel,
  CounterDisplay,
  ConfirmButton,
  CancelButton,
} from "./styles";

interface TermSection {
  id: string;
  num: string;
  title: string;
  note?: string;
  html: string;
  accept: string;
}

interface TermsModalProps {
  open: boolean;
  onClose: () => void;
  onAccept: () => void;
}

const SECTIONS: TermSection[] = [
  {
    id: "plataforma",
    num: "01",
    title: "Inscrição, Pagamento e Dados",

    html: `
      <p>A <strong>TorneioFácil</strong> é apenas a plataforma de gestão de inscrições — não organiza nem produz os eventos. As inscrições são <strong>pessoais e intransferíveis</strong>.</p>
      <p>Informações falsas (nome, nascimento, faixa, peso etc.) podem causar desclassificação, banimento e processo civil. É sua responsabilidade conferir seus dados na <strong>Checagem Geral Aberta</strong> e acompanhar comunicados sobre alterações no evento.</p>
      <p>A organização e o professor da equipe podem editar dados da sua inscrição (academia, apelido etc.).</p>
      <p><strong>Reembolso</strong> não é permitido após o pagamento, salvo exceções nos detalhes do evento. Boletos levam até 3 dias úteis; pagamentos fora do prazo não garantem participação.</p>
      <p><strong>Dados pessoais:</strong> serão compartilhados com a organização do evento e tratados conforme a LGPD (Lei 13.709/2018). Enviaremos comunicações por e-mail — confira sua caixa de entrada e spam regularmente.</p>
    `,
    accept: "Li e aceito os termos de inscrição, pagamento e dados.",
  },
  {
    id: "riscos",
    num: "02",
    title: "Riscos, Saúde e Responsabilidade",

    html: `
      <p>Competições de artes marciais oferecem <strong>risco de contusões graves</strong>, incluindo perda de movimentos e, em casos extremos, morte. Aceito esses riscos voluntariamente e <strong>isento a organização e a TorneioFácil</strong> de qualquer responsabilidade por lesões, independente da causa.</p>
      <p>Renuncio ao direito de mover processos judiciais por contusões no evento. Conflitos serão resolvidos por <strong>arbitragem</strong> junto à comissão disciplinar da organização.</p>
      <p>Declaro estar em <strong>boas condições de saúde</strong>, apto(a) a competir, sem uso de substâncias que comprometam minha capacidade. Autorizo a equipe médica do evento a prestar atendimento se necessário.</p>
    `,
    accept: "Li e aceito os termos de riscos, saúde e responsabilidade.",
  },
  {
    id: "gestante",
    num: "03",
    title: "Declaração de Não Gestação",

    note: "Aplicável a atletas do sexo feminino.",
    html: `
      <p>Declaro que <strong>NÃO estou grávida</strong> na data do evento. Competir durante a gestação representa risco grave à saúde da gestante e do feto (aborto, parto prematuro, descolamento de placenta etc.).</p>
      <p>Caso esta declaração seja falsa, estarei sujeita a desclassificação sem reembolso e responsabilização civil/criminal. A organização e a TorneioFácil ficam totalmente isentas.</p>
    `,
    accept: "Declaro que NÃO estou grávida e aceito estes termos.",
  },
  {
    id: "imagem",
    num: "04",
    title: "Imagem e Antidoping",

    html: `
      <p>Autorizo a captação e uso da minha <strong>imagem</strong> (foto e vídeo) durante o evento, para divulgação em qualquer meio, sem compensação financeira.</p>
      <p><strong>Antidoping</strong> (quando aplicável): concordo em submeter-me a controle antidoping se exigido pelo evento. Substâncias ou métodos proibidos podem causar desqualificação e suspensão.</p>
    `,
    accept: "Li e aceito os termos de imagem e antidoping.",
  },
  {
    id: "menores",
    num: "05",
    title: "Menores de Idade",

    note: "Exibido quando o atleta é menor de 18 anos.",
    html: `
      <p>Como pai/mãe ou responsável legal, declaro que li e aceitei <strong>todas as seções</strong> deste documento em nome do menor e em meu próprio nome, assumindo todos os riscos.</p>
      <p>A conta do menor deve ser <strong>vinculada à conta do responsável</strong> na TorneioFácil, com documentos que comprovem parentesco. O vínculo será exigido na renovação do cadastro.</p>
    `,
    accept: "Como responsável legal, aceito todos os termos em nome do menor.",
  },
  {
    id: "filiacao",
    num: "06",
    title: "Filiação em Federação",

    note: "Exibido quando o atleta realiza filiação via plataforma.",
    html: `
      <p>Ao filiar-me via TorneioFácil, submeto meu registro à federação selecionada e me comprometo a manter dados atualizados, respeitar o regulamento, competir com ética e cumprir obrigações financeiras.</p>
      <p>As regras da federação podem mudar — é minha responsabilidade acompanhá-las.</p>
    `,
    accept: "Li e aceito os termos de filiação em federação.",
  },
];

const TermsModal: React.FC<TermsModalProps> = ({ open, onClose, onAccept }) => {
  const [accepted, setAccepted] = useState<Record<string, boolean>>({});
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    plataforma: true,
  });

  const total = SECTIONS.length;
  const doneCount = Object.values(accepted).filter(Boolean).length;
  const allAccepted = doneCount === total;
  const progress = Math.round((doneCount / total) * 100);

  const toggleSection = useCallback((id: string) => {
    setOpenSections((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const toggleAccept = useCallback((id: string) => {
    setAccepted((prev) => {
      const wasAccepted = prev[id];
      const next = { ...prev, [id]: !wasAccepted };

      // Se está aceitando (não desaceitando), abre a próxima seção e fecha a atual
      if (!wasAccepted) {
        const currentIndex = SECTIONS.findIndex((s) => s.id === id);
        const nextSection = SECTIONS[currentIndex + 1];

        setOpenSections((prevOpen) => {
          const updated = { ...prevOpen, [id]: false };
          if (nextSection) {
            updated[nextSection.id] = true;
          }
          return updated;
        });
      }

      return next;
    });
  }, []);

  const handleConfirm = () => {
    if (allAccepted) {
      onAccept();
    }
  };

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!open) return null;

  return (
    <ModalOverlay onClick={handleOverlayClick}>
      <ModalContainer>
        <ModalHeader>
          <h2>Termos e Condições</h2>
          <p>Leia e aceite cada seção para prosseguir com sua inscrição.</p>
        </ModalHeader>

        <ModalBody>
          <ProgressBar>
            <ProgressTrack>
              <ProgressFill progress={progress} />
            </ProgressTrack>
            <ProgressLabel>
              <span translate="no">{doneCount}</span> / <span translate="no">{total}</span>
            </ProgressLabel>
          </ProgressBar>

          {SECTIONS.map((section) => (
            <Section key={section.id} accepted={!!accepted[section.id]}>
              <SectionHeader onClick={() => toggleSection(section.id)}>
                <SectionNumber accepted={!!accepted[section.id]}>
                  {section.num}
                </SectionNumber>
                <SectionTitle>
                  {section.title}
                </SectionTitle>
                <Chevron
                  open={!!openSections[section.id]}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="m6 9 6 6 6-6" />
                </Chevron>
              </SectionHeader>

              <SectionBody open={!!openSections[section.id]}>
                <SectionContent>
                  {section.note && (
                    <NoteBox>
                      <span>⚠</span>
                      <span>{section.note}</span>
                    </NoteBox>
                  )}
                  <div dangerouslySetInnerHTML={{ __html: section.html }} />
                  <AcceptBox
                    checked={!!accepted[section.id]}
                    onClick={() => toggleAccept(section.id)}
                  >
                    <CustomCheck checked={!!accepted[section.id]}>
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#fff"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M20 6 9 17l-5-5" />
                      </svg>
                    </CustomCheck>
                    <AcceptLabel checked={!!accepted[section.id]}>
                      {section.accept}
                    </AcceptLabel>
                  </AcceptBox>
                </SectionContent>
              </SectionBody>
            </Section>
          ))}
        </ModalBody>

        <ModalFooter>
          <CounterDisplay>
            <span className="done" translate="no">{doneCount}</span>
            <span className="total"> / <span translate="no">{total}</span></span>
          </CounterDisplay>
          <ConfirmButton
            active={allAccepted}
            disabled={!allAccepted}
            onClick={handleConfirm}
          >
            <svg
              width="16"
              height="16"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              viewBox="0 0 24 24"
            >
              <path d="M20 6 9 17l-5-5" />
            </svg>
            Confirmar e Prosseguir
          </ConfirmButton>
          <CancelButton onClick={onClose}>Cancelar</CancelButton>
        </ModalFooter>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default TermsModal;
